import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  Grid,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/auth';
import { usePermissions } from '../context/PermissionContext';

const EmploymentLetter = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { hasPermission } = usePermissions();

  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [employee, setEmployee] = useState(null);
  const [letter, setLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchEmployeeAndLetter();
  }, [id]);

  const fetchEmployeeAndLetter = async () => {
    setLoading(true);
    try {
      // Fetch employee details
      const employeeResponse = await axios.get(`${baseUrl}/api/employees/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployee(employeeResponse.data.employee);

      // Generate employment letter
      const letterResponse = await axios.post(
        `${baseUrl}/api/employment-letters/generate/${id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLetter(letterResponse.data.letter);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 404 && error.response?.data?.error?.includes('template')) {
        setError('No employment letter template found. Please create a template first.');
      } else {
        setError(error.response?.data?.error || 'Failed to generate employment letter');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // Create a printable version
    const printWindow = window.open('', '', 'width=800,height=600');
    const letterContent = document.getElementById('letter-content').innerHTML;

    printWindow.document.write(`
      <html>
        <head>
          <title>Employment Letter - ${employee?.employeeId}</title>
          <style>
            @page {
              margin: 0;
              size: auto;
            }
            * {
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px 30px;
              margin: 0;
              max-width: 210mm;
              margin: 0 auto;
              color: #333;
            }
            img {
              width: 100px !important;
              height: 100px !important;
              object-fit: contain;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            /* Header with logos - only target the header container */
            body > div:first-child {
              display: flex !important;
              align-items: center !important;
              justify-content: space-between !important;
              padding-bottom: 8px !important;
              margin-bottom: 16px !important;
              border-bottom: 1px solid #333 !important;
            }
            /* Header text styling */
            h4, h5, .MuiTypography-h4, .MuiTypography-h5 {
              text-align: center !important;
              font-weight: bold !important;
              margin-bottom: 5px !important;
              font-size: 1.5rem !important;
              color: #000 !important;
            }
            /* Header institution details - centered */
            body > div:first-child .MuiBox-root:nth-child(2) {
              text-align: center !important;
            }
            body > div:first-child .MuiTypography-body1,
            body > div:first-child .MuiTypography-body2 {
              text-align: center !important;
            }
            .MuiTypography-body1 {
              font-size: 0.9rem !important;
              margin-bottom: 3px !important;
            }
            .MuiTypography-body2 {
              font-size: 0.85rem !important;
              color: #666 !important;
            }
            /* Body text styling */
            p {
              text-align: justify !important;
              line-height: 1.6 !important;
              margin-bottom: 12px !important;
              color: #333 !important;
            }
            strong, b {
              font-weight: bold !important;
              color: #000 !important;
            }
            h3, h4 {
              font-weight: bold !important;
              margin-top: 16px !important;
              margin-bottom: 8px !important;
              color: #000 !important;
            }
            /* Remove default Material-UI classes that might interfere */
            .MuiBox-root {
              page-break-inside: avoid;
            }
            /* Signature line styling */
            .signature-line {
              width: 200px;
              border-bottom: 1px solid #333;
              margin-bottom: 8px;
              margin-top: 24px;
            }
            /* Footer styling */
            .footer-text {
              text-align: center;
              color: #999 !important;
              font-size: 0.7rem !important;
              margin-top: 16px;
              padding-top: 16px;
              border-top: 1px solid #e0e0e0;
            }
            hr {
              border: none;
              border-top: 1px solid #e0e0e0;
              margin: 30px 0 15px 0;
            }
          </style>
        </head>
        <body>
          ${letterContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard/employees')}
            variant="outlined"
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Employment Letter
          </Typography>
        </Box>
        <Alert severity="error">
          {error}
          {error.includes('template') && hasPermission('employment_letter', 'create') && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={() => navigate('/dashboard/employment-letter-template')}
              >
                Create Template
              </Button>
            </Box>
          )}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Action Bar - Don't print this */}
      <Box className="no-print" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard/employees')}
            variant="outlined"
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Employment Letter
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {hasPermission('employment_letter', 'edit') && (
            <Button
              startIcon={<EditIcon />}
              onClick={() => navigate('/dashboard/employment-letter-template')}
              variant="outlined"
            >
              Edit Template
            </Button>
          )}
          <Button
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            variant="outlined"
            color="primary"
          >
            Print
          </Button>
          <Button
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            variant="contained"
            color="primary"
          >
            Download
          </Button>
        </Box>
      </Box>

      {/* Employee Summary - Don't print this */}
      <Paper className="no-print" elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
          Employee Information
        </Typography>
        <Grid container spacing={3}>
          {/* Profile Picture */}
          <Grid item xs={12} sm={3} sx={{ textAlign: 'center' }}>
            <Avatar
              src={employee?.profilePicture ? `${baseUrl}${employee.profilePicture}` : undefined}
              sx={{ width: 120, height: 120, margin: '0 auto', mb: 2 }}
            >
              {employee?.firstName?.[0]}{employee?.lastName?.[0]}
            </Avatar>
            <Typography variant="body1" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
              {employee?.employeeId}
            </Typography>
          </Grid>

          {/* Employee Details - 3 per row */}
          <Grid item xs={12} sm={9}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">Full Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {employee?.firstName} {employee?.middleName} {employee?.lastName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">Job Title</Typography>
                <Typography variant="body1">{employee?.jobTitle}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">Date of Hire</Typography>
                <Typography variant="body1">
                  {employee?.dateOfHire && new Date(employee.dateOfHire).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Typography variant="body1">{employee?.email}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">Phone Number</Typography>
                <Typography variant="body1">{employee?.phoneNumber}</Typography>
              </Grid>
              {employee?.educationalBackground && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Education</Typography>
                  <Typography variant="body1">{employee.educationalBackground}</Typography>
                </Grid>
              )}
              {employee?.maritalStatus && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Marital Status</Typography>
                  <Typography variant="body1">{employee.maritalStatus}</Typography>
                </Grid>
              )}
              {employee?.address && (
                <Grid item xs={12} sm={8}>
                  <Typography variant="caption" color="text.secondary">Address</Typography>
                  <Typography variant="body1">
                    {employee.address}
                    {employee.district && `, ${employee.district}`}
                    {employee.county && `, ${employee.county}`}
                    {employee.countryOfOrigin && `, ${employee.countryOfOrigin}`}
                  </Typography>
                </Grid>
              )}
              {(employee?.emergencyContactName || employee?.emergencyContactNumber) && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Emergency Contact</Typography>
                  <Typography variant="body1">
                    {employee.emergencyContactName && `${employee.emergencyContactName}`}
                    {employee.emergencyContactName && employee.emergencyContactNumber && ' - '}
                    {employee.emergencyContactNumber && `${employee.emergencyContactNumber}`}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Letter Content - This will be printed */}
      <Paper id="letter-content" elevation={3} sx={{ p: 4, backgroundColor: 'white' }}>
        {/* Header with logos on both sides */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          pb: 1,
          borderBottom: '1px solid #333',
        }}>
          {/* Left Logo */}
          {letter?.logoPath && (
            <Box sx={{ flex: '0 0 auto' }}>
              <img
                src={`${baseUrl}${letter.logoPath}`}
                alt="Institution Logo"
                style={{ width: '100px', height: '100px', objectFit: 'contain' }}
              />
            </Box>
          )}

          {/* Center - Institution Header */}
          <Box sx={{ flex: 1, textAlign: 'center', px: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 'bold',
                mb: 1,
                fontSize: '1.75rem',
                color: '#000',
                letterSpacing: '0.5px'
              }}
            >
              {letter?.institutionName}
            </Typography>
            {/* Institution Contact Information */}
            {letter?.institutionAddress && (
              <Typography
                variant="body1"
                sx={{
                  mb: 0.5,
                  color: '#666',
                  fontSize: '1rem'
                }}
              >
                {letter.institutionAddress}
              </Typography>
            )}
            {(letter?.institutionEmail || letter?.institutionPhone) && (
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  fontSize: '0.95rem'
                }}
              >
                {letter?.institutionEmail && `Email: ${letter.institutionEmail}`}
                {letter?.institutionEmail && letter?.institutionPhone && ' | '}
                {letter?.institutionPhone && `Tel: ${letter.institutionPhone}`}
              </Typography>
            )}
          </Box>

          {/* Right Logo */}
          {letter?.logoPath && (
            <Box sx={{ flex: '0 0 auto' }}>
              <img
                src={`${baseUrl}${letter.logoPath}`}
                alt="Institution Logo"
                style={{ width: '100px', height: '100px', objectFit: 'contain' }}
              />
            </Box>
          )}
        </Box>

        {/* Letter Body */}
        <Box
          sx={{
            '& p': { marginBottom: 1.5, textAlign: 'justify', lineHeight: 1.6 },
            '& h1, & h2, & h3, & h4, & h5, & h6': { marginTop: 2, marginBottom: 1.5, fontWeight: 'bold' },
            '& strong': { fontWeight: 'bold' },
            lineHeight: 1.6,
            px: 2,
            mt: 2,
            color: '#333',
          }}
          dangerouslySetInnerHTML={{ __html: letter?.content }}
        />

        {/* Signature Section */}
        <Box sx={{ mt: 3, px: 2 }}>
          {letter?.signByName && (
            <>
              <Box sx={{ width: '200px', borderBottom: '1px solid #333', mb: 1, mt: 3 }} />
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {letter.signByName}
              </Typography>
              {letter?.signByTitle && (
                <Typography variant="body2">
                  {letter.signByTitle}
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #e0e0e0', textAlign: 'center', mx: 2 }}>
          <Typography variant="caption" sx={{ color: '#999', fontSize: '0.7rem' }}>
            Generated on {new Date(letter?.generatedAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </Typography>
        </Box>
      </Paper>

      {/* Print Styles */}
      <style>
        {`
          @media print {
            @page {
              margin: 0;
              size: auto;
            }
            .no-print {
              display: none !important;
            }
            body {
              margin: 0;
              padding: 0;
            }
            #letter-content {
              box-shadow: none !important;
              margin: 0 auto !important;
              padding: 20px 30px !important;
              max-width: 210mm !important;
            }
            /* Ensure header layout in print */
            #letter-content > div:first-child {
              display: flex !important;
              align-items: center !important;
              justify-content: space-between !important;
              padding-bottom: 8px !important;
              margin-bottom: 16px !important;
              border-bottom: 1px solid #333 !important;
            }
            /* Ensure logos print correctly */
            #letter-content img {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
              width: 100px !important;
              height: 100px !important;
            }
            /* Header text styling - only for the institution name in header */
            #letter-content > div:first-child h4 {
              text-align: center !important;
              font-size: 1.5rem !important;
              font-weight: bold !important;
              color: #000 !important;
            }
            /* Header institution details - centered */
            #letter-content > div:first-child > div:nth-child(2) {
              text-align: center !important;
            }
            #letter-content > div:first-child .MuiTypography-body1,
            #letter-content > div:first-child .MuiTypography-body2 {
              text-align: center !important;
            }
            /* Body heading styling - for Terms of Reference etc */
            h3, h4 {
              text-align: left !important;
              font-size: 1rem !important;
              font-weight: bold !important;
              color: #000 !important;
              margin-top: 16px !important;
              margin-bottom: 8px !important;
            }
            /* Body text styling */
            p {
              text-align: justify !important;
              line-height: 1.6 !important;
              margin-bottom: 12px !important;
              color: #333 !important;
            }
            strong {
              font-weight: bold !important;
            }
            /* Avoid page breaks */
            #letter-content > div {
              page-break-inside: avoid;
            }
            /* Signature line styling */
            .MuiBox-root .MuiBox-root[style*="border-bottom"] {
              border-bottom: 1px solid #333 !important;
            }
            /* Footer border and text styling */
            #letter-content > div:last-child {
              border-top: 1px solid #e0e0e0 !important;
              text-align: center !important;
            }
            #letter-content > div:last-child .MuiTypography-caption {
              color: #999 !important;
              font-size: 0.7rem !important;
            }
          }
        `}
      </style>
    </Container>
  );
};

export default EmploymentLetter;
